import { ISettingsParam } from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { SubtitleSourceOptions } from '../../types/types';
import {
  ISettingsParams,
  ISettingsParamsDefinitions,
} from './settingsParams';
import {
  IStyleParamsBoolean,
  IStyleParamsVariants,
} from './stylesParams';

export const getFromSettingsParams = ({
  settingsParams,
  tpaData,
  isMobile,
  key,
}: {
  settingsParams: Partial<ISettingsParamsDefinitions>;
  tpaData: any;
  isMobile: boolean;
  key: keyof ISettingsParams;
}) => {
  const settingParam = settingsParams[key];
  return (
    (isMobile
      ? settingParam?.inheritDesktop ?? true
        ? tpaData[`${key}▶︎m`] ?? tpaData[key]
        : tpaData[`${key}▶︎m`]
      : tpaData[key]) ??
    (settingParam && typeof settingParam.getDefaultValue === 'function'
      ? settingParam.getDefaultValue({
          isMobile,
          getSettingParamValue: (
            param: ISettingsParam,
            options?: { isMobile?: boolean; isRTL?: boolean; isEditorX?: boolean },
          ) =>
            getFromSettingsParams({
              isMobile,
              settingsParams,
              tpaData,
              key: param.key as keyof ISettingsParams,
            }),
        } as any)
      : undefined)
  );
};

export const generateCustomCssVars = (
  args: Parameters<CustomCssVarsFn>[0],
  settingsParams: Partial<ISettingsParamsDefinitions>,
) => {
  const { styleParams, tpaData, isMobile } = args;

  const getBooleanStyleParam = (
    key: keyof IStyleParamsBoolean,
  ): boolean | undefined => {
    return styleParams.booleans[key];
  };

  const getNumberStyleParam = (
    key: keyof IStyleParamsVariants,
  ): number | undefined => {
    return styleParams.numbers[key];
  };

  const displayVariants = (
    key: keyof IStyleParamsVariants,
    totalNumberOfVariants: number,
  ) => {
    const variant =
      getNumberStyleParam(key) ??
      (getFromSettingsParams({
        settingsParams,
        tpaData,
        isMobile,
        key,
      }) as number);

    const variants: { [key: string]: string } = {};

    for (let i = 0; i < totalNumberOfVariants; i++) {
      variants[`${key}_${i}`] = i === variant ? 'block' : 'unset';
    }

    return variants;
  };

  const numberOfKeysInEnum = (enumType: any): number =>
    Object.keys(enumType).filter((key) => isNaN(Number(key))).length;

  const displayStyle = (key: keyof IStyleParamsBoolean & keyof ISettingsParams) => {
    let isVisible: boolean;
    
    // For backward compatibility: check settings first
    const settingsValue = getFromSettingsParams({
      settingsParams,
      tpaData,
      isMobile,
      key,
    });
    
    isVisible = typeof settingsValue === 'boolean' ? settingsValue : getBooleanStyleParam(key) as boolean;
    
    return isVisible
      ? {
          [key + 'Hidden']: 'unset',
          [key + 'DisplayBlock']: 'block',
          [key + 'DisplayFlex']: 'flex',
        }
      : {
          [key + 'Hidden']: 'none',
          [key + 'DisplayBlock']: 'unset',
          [key + 'DisplayFlex']: 'unset',
        };
  };

  const simpleDisplayStyle = (
    booleanStyleParam: keyof IStyleParamsBoolean,
    visibleDisplay = 'flex',
  ) => (styleParams.booleans[booleanStyleParam] ? visibleDisplay : 'none');


  return {
    ...displayStyle('headerSubtitleVisibility'),
    ...displayStyle('headerTitleVisibility'),
    ...displayStyle('headerLocationFilterVisibility'),
    ...displayStyle('headerStaffFilterVisibility'),
    ...displayStyle('headerServiceFilterVisibility'),
    ...displayStyle('headerVisibility'),
    ...displayStyle('headerTimezoneSelectionVisibility'),
    ...displayStyle('timeslotDetailsVisibility'),
    ...displayStyle('videoConferenceBadgeVisibility'),
    ...displayStyle('dateAndTimeTimezoneSelectionVisibility'),
    ...displayVariants(
      'headerSubtitleSource',
      numberOfKeysInEnum(SubtitleSourceOptions),
    ),
    slotPriceBeforeDiscountDisplay: simpleDisplayStyle('slotPriceBeforeDiscountVisibility'),
    slotDiscountNameDisplay: simpleDisplayStyle('slotDiscountNameVisibility'),
  };
};
